import { format, formatDistanceToNow, isYesterday, isToday } from 'date-fns';

/**
 * Formats a Naira amount.
 * e.g. 85000000 → "₦85,000,000.00"
 */
export function formatNaira(amount: number | null): string {
  if (!amount && amount !== 0) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
  }).format(amount);
}

/**
 * Compact Naira format for dashboard stats.
 * e.g. 85000000 → "₦85M", 4200000 → "₦4.2M", 850000 → "₦850K"
 */
export function formatNairaCompact(amount: number | null): string {
  if (!amount && amount !== 0) return '₦0';

  if (amount >= 1_000_000_000) {
    const val = amount / 1_000_000_000;
    return `₦${val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)}B`;
  }
  if (amount >= 1_000_000) {
    const val = amount / 1_000_000;
    return `₦${val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    const val = amount / 1_000;
    return `₦${val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)}K`;
  }
  return `₦${amount.toFixed(0)}`;
}

/**
 * Consistent date formatting.
 * e.g. "Mar 28, 2026"
 */
export function formatDate(date: string | Date): string {
  return format(new Date(date), 'MMM d, yyyy');
}

/**
 * Relative date formatting.
 * e.g. "2 hours ago", "Yesterday", "3 days ago"
 */
export function formatRelativeDate(date: string | Date): string {
  const d = new Date(date);
  if (isToday(d)) {
    return formatDistanceToNow(d, { addSuffix: true });
  }
  if (isYesterday(d)) {
    return 'Yesterday';
  }
  return formatDistanceToNow(d, { addSuffix: true });
}

/**
 * Formats Nigerian phone numbers consistently.
 * Converts 0801... to +234 801 ... and formats with spaces.
 */
export function formatPhoneNG(phone: string | null): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');

  let digits = cleaned;
  if (digits.startsWith('234') && digits.length === 13) {
    digits = digits.slice(3);
  } else if (digits.startsWith('0') && digits.length === 11) {
    digits = digits.slice(1);
  }

  if (digits.length !== 10) return phone; // return as-is if unexpected format

  return `+234 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}
